<template>
  <canvas :id="componentId"></canvas>
</template>

<script>
import Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { abstractField } from 'vue-form-generator';
import pageServices from '@/services/page';
import { returnNodeDataRequest, executeServicesData } from '@/helpers/page';
import { isEqual } from 'lodash';
import * as HANDLERS from '@/helpers/types/handlers';

Chart.plugins.unregister(ChartDataLabels);

export default {
  mixins: [abstractField],

  events: {
    [HANDLERS.RELOAD_GRAPHICS]: 'init'
  },

  data() {
    return {
      chartInstance: null,
    };
  },

  mounted() {
    this.init();
  },

  computed: {
    componentModel() {
      return this.schema.model;
    },

    componentId() {
      // concatena o nome do modelo com o nome do componente para gerar um id único
      // esse id é utilizado para forçar o recarregamento do gráfico durante o loadMode
      return this.componentModel + "-i4pro-graphic-chartjs";
    },

    modelValue() {
      return this.model[this.componentModel];
    }
  },

  watch: {
    componentModel: {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.init()
        }
      },
    },

    modelValue(newVal, oldVal) {
      if (isEqual(newVal, oldVal)) {
        return;
      }

      this.init(true)
    }
  },

  methods: {
    init(update = false) {
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      this.setup(update);
    },

    async setup(update = false) {
      await this.getConfig();
      await this.getData(update);
      this.render();
    },

    async getConfig() {
      if(!('procedure' in this.schema)) {
        return;
      }

      const { typeRequest, bodyRequest } = returnNodeDataRequest( 
        { schema: this.schema },
        { form: this.model, filter: {}, defaultFilter: {}, page: this.formOptions?.page, isRedirect: false, InvokeName: 'Get Config Chat' }
      );

      if (this.schema[typeRequest] && 'config' in this.schema[typeRequest]) {
        let bodyProcedure = {};
        if (typeRequest === 'procedure') {
          bodyProcedure = {
            name: this.schema.procedure.config,
            model: this.model
          };
        }

        const { data } = await executeServicesData(typeRequest, { ...bodyRequest, ...bodyProcedure });

        if (data.values.length) {
          const { labels, backgroundColor, borderColor, borderWidth, type } = data.values[0];

          this.schema.graphic.type = type;
          this.schema.graphic.data.labels = labels.split(';');
          this.schema.graphic.data.datasets[0].backgroundColor = backgroundColor.split(';');
          this.schema.graphic.data.datasets[0].borderColor = borderColor.split(';');
          this.schema.graphic.data.datasets[0].borderWidth = borderWidth;
        }
      }
    },

    async getData(update) {
      if (update && this.model[this.componentModel]) {
        if (typeof this.model[this.componentModel] === 'string') {
          this.schema.graphic = JSON.parse(this.model[this.componentModel])
        } else {
          this.schema.graphic = this.model[this.componentModel];
        }
        
        return
      }

      if ('procedure' in this.schema) {
        if ('data' in this.schema.procedure) {
          const params = {
            name: this.schema.procedure.data,
            model: {},
            page: this.formOptions.page
          };
  
          const { data } = await pageServices.graphicData(params);
  
          if (data.length) {
            this.schema.graphic.data.labels = data[0].map(element => element.group);
            this.schema.graphic.data.datasets = [];
  
            data.forEach(element => {
              let dataset = {
                label: '',
                backgroundColor: [],
                data: []
              };
  
              element.forEach(item => {
                dataset.label = item.label;
                dataset.backgroundColor = [...dataset.backgroundColor, item.backgroundColor];
                dataset.data = [...dataset.data, item.value];
              });
  
              this.schema.graphic.data.datasets.push(dataset);
            });
          }
  
          return
        }

        if ('name' in this.schema.procedure) {
          const data = await this.getGraphicData();
          this.setGraphicSchema(data, 'procedure');
          return
        }
      }

      if ('requestApi' in this.schema) {
        const data = await this.getGraphicData();
        this.setGraphicSchema(data, 'requestApi');
        return
      }

      if ((this.isEmptyObject(this.schema.graphic) || update) && this.model[this.componentModel]) {
        this.schema.graphic = this.model[this.componentModel];
      }
    },

    async getGraphicData() {
      const { typeRequest, bodyRequest } = returnNodeDataRequest( 
        { schema: this.schema },
        { form: this.model, filter: {}, defaultFilter: {}, page: this.formOptions?.page, isRedirect: false, InvokeName: 'Get Config Chat' }
      );

      const { data } = await executeServicesData(typeRequest, bodyRequest);
      return data
    },

    setGraphicSchema(data, type) {
      if (data.values && data.values.length) {
        if (this.componentModel in data.values[0]) {
          if (type === 'procedure') {
            this.schema.graphic = JSON.parse(data.values[0][this.componentModel])
          }

          if (type === 'requestApi') {
            this.schema.graphic = data.values[0][this.componentModel]
          }
        } else {
          this.schema.graphic = data.values[0]
        }
      }
    },

    convertStringToFunction(obj) {
      if (!obj || typeof obj !== 'object') return;

      for (const key in obj) {
        if (typeof obj[key] === 'string' && obj[key].startsWith('function')) {
          obj[key] = new Function('return ' + obj[key])();
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          this.convertStringToFunction(obj[key]);
        }
      }
    },

    render() {
      const ctx = document.getElementById(this.componentId).getContext('2d');

      // o método structuredClone é necessário para enviar ao construtor uma cópia do objeto não reativa
      const type = structuredClone(this.schema.graphic.type);
      const data = structuredClone(this.schema.graphic.data);
      const options = structuredClone(this.schema.graphic.options);

      // Converte todas as strings de funções em funções reais
      this.convertStringToFunction(options);

      let plugins = []
      // Adiciona o plugin diretamente nas opções do gráfico, se necessário
      if (options && options.plugins && options.plugins.datalabels) {
        plugins = ChartDataLabels;
      }

      this.chartInstance = new Chart(ctx, {
        plugins,
        type,
        data,
        options
      });
    },

    isEmptyObject(obj) {
      return Object.keys(obj).length === 0 && obj.constructor === Object;
    },
  }
};
</script>
